import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { customerService, Customer } from '../services/customer';
import {
  UserIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowLeftIcon,
  EnvelopeIcon,
  PhoneIcon,
  CalendarIcon,
  CameraIcon,
  SparklesIcon,
  PencilIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { locationService, Location } from '../services/location';
import StaffPicksManager from '../components/StaffPicksManager';

const CustomerDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [updating, setUpdating] = useState(false);
  const [message, setMessage] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({ name: '', phone: '' });
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [locations, setLocations] = useState<Location[]>([]);

  // Only super-admins (staff not scoped to a store) may reassign a customer's store.
  const canEditStore = !!user && !user.locationId;

  useEffect(() => {
    if (id) {
      loadCustomer();
    }
  }, [id]);

  useEffect(() => {
    if (canEditStore) {
      locationService
        .getLocations()
        .then((all) => setLocations(all.filter((l) => l.isActive)))
        .catch((err) => console.error('Failed to load stores:', err));
    }
  }, [canEditStore]);

  const loadCustomer = async () => {
    try {
      setLoading(true);
      if (id) {
        const data = await customerService.getCustomer(id);
        setCustomer(data);
        setEditData({ name: data.name, phone: data.phone || '' });
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load customer');
    } finally {
      setLoading(false);
    }
  };

  const handleAccessLevelChange = async (newLevel: 'new' | 'paid_deposit' | 'exclusive') => {
    if (!customer) return;

    const levelNames: Record<string, string> = {
      'new': 'New',
      'paid_deposit': 'Paid Deposit',
      'exclusive': 'Exclusive'
    };

    try {
      setUpdating(true);
      await customerService.updateCustomer(customer.id, { catalogAccessLevel: newLevel });
      setCustomer({ ...customer, catalogAccessLevel: newLevel });
      setMessage(`Catalogue access level changed to ${levelNames[newLevel]}`);
      setTimeout(() => setMessage(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update catalogue access level');
    } finally {
      setUpdating(false);
    }
  };

  const handleStoreChange = async (newLocationId: string) => {
    if (!customer) return;

    try {
      setUpdating(true);
      const updated = await customerService.updateCustomer(customer.id, {
        locationId: newLocationId || null,
      });
      setCustomer({ ...customer, locationId: updated.locationId, location: updated.location });
      setMessage('Store updated successfully');
      setTimeout(() => setMessage(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update store');
    } finally {
      setUpdating(false);
    }
  };

  const handleResendVerification = async () => {
    if (!customer) return;
    
    try {
      setUpdating(true);
      await customerService.resendVerificationEmail(customer.id);
      setMessage('Verification email sent successfully');
      setTimeout(() => setMessage(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to resend verification email');
    } finally {
      setUpdating(false);
    }
  };

  const handleManualVerification = async () => {
    if (!customer) return;
    
    try {
      setUpdating(true);
      await customerService.verifyEmailManually(customer.id);
      setCustomer({ ...customer, emailVerified: true });
      setMessage('Email verified manually');
      setTimeout(() => setMessage(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to verify email manually');
    } finally {
      setUpdating(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!customer) return;
    
    try {
      setUpdating(true);
      await customerService.updateCustomer(customer.id, editData);
      setCustomer({ ...customer, ...editData });
      setEditMode(false);
      setMessage('Customer details updated successfully');
      setTimeout(() => setMessage(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update customer');
    } finally {
      setUpdating(false);
    }
  };

  const handleToggleStatus = async () => {
    if (!customer) return;
    
    try {
      setUpdating(true);
      await customerService.updateCustomerStatus(customer.id, !customer.isActive);
      setCustomer({ ...customer, isActive: !customer.isActive });
      setMessage(`Customer account ${!customer.isActive ? 'activated' : 'deactivated'} successfully`);
      setTimeout(() => setMessage(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update customer status');
    } finally {
      setUpdating(false);
    }
  };

  const handleDelete = async () => {
    if (!customer || !deleteConfirm) return;
    
    try {
      setUpdating(true);
      await customerService.deleteCustomer(customer.id);
      navigate('/customers');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete customer');
      setDeleteConfirm(false);
    } finally {
      setUpdating(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const day = date.getDate();
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="text-center py-12">
        <div className="text-lg text-gray-600">Customer not found</div>
        <Link to="/customers" className="mt-4 text-indigo-600 hover:text-indigo-500">
          Back to Customers
        </Link>
      </div>
    );
  }

  return (
    <div className="px-4 py-6 sm:px-0">
      {/* Header */}
      <div className="mb-6">
        <Link
          to="/customers"
          className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-1" />
          Back to Customers
        </Link>
        <h1 className="text-2xl font-bold text-gray-900">Customer Details</h1>
      </div>

      {/* Messages */}
      {message && (
        <div className="mb-6 rounded-md bg-green-50 p-4">
          <div className="text-sm text-green-700">{message}</div>
        </div>
      )}

      {error && (
        <div className="mb-6 rounded-md bg-red-50 p-4">
          <div className="text-sm text-red-700">{error}</div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Customer Profile */}
        <div className="lg:col-span-2">
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-900">Profile Information</h2>
            </div>
            <div className="px-6 py-4">
              <div className="flex items-center mb-6">
                <div className="h-16 w-16 rounded-full bg-gray-300 flex items-center justify-center mr-4">
                  <UserIcon className="h-8 w-8 text-gray-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-gray-900">{customer.name}</h3>
                  <p className="text-gray-600">{customer.email}</p>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={handleToggleStatus}
                    disabled={updating}
                    className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                      customer.isActive
                        ? 'bg-green-100 text-green-800 hover:bg-green-200'
                        : 'bg-red-100 text-red-800 hover:bg-red-200'
                    } disabled:opacity-50`}
                  >
                    {customer.isActive ? 'Active' : 'Inactive'}
                  </button>
                  {editMode ? (
                    <>
                      <button
                        onClick={handleSaveEdit}
                        disabled={updating}
                        className="text-green-600 hover:text-green-900 disabled:opacity-50"
                        title="Save Changes"
                      >
                        <CheckCircleIcon className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => {
                          setEditMode(false);
                          setEditData({ name: customer.name, phone: customer.phone || '' });
                        }}
                        className="text-red-600 hover:text-red-900"
                        title="Cancel Edit"
                      >
                        <XCircleIcon className="h-5 w-5" />
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => setEditMode(true)}
                      className="text-indigo-600 hover:text-indigo-900"
                      title="Edit Customer Details"
                    >
                      <PencilIcon className="h-5 w-5" />
                    </button>
                  )}
                </div>
              </div>

              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Name</dt>
                  <dd className="mt-1">
                    {editMode ? (
                      <input
                        type="text"
                        value={editData.name}
                        onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                        className="w-full px-3 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                    ) : (
                      <span className="text-sm text-gray-900">{customer.name}</span>
                    )}
                  </dd>
                </div>

                <div>
                  <dt className="text-sm font-medium text-gray-500">Phone</dt>
                  <dd className="mt-1">
                    {editMode ? (
                      <input
                        type="tel"
                        value={editData.phone}
                        onChange={(e) => setEditData({ ...editData, phone: e.target.value })}
                        placeholder="Not provided"
                        className="w-full px-3 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                    ) : (
                      <span className="text-sm text-gray-900">{customer.phone || 'Not provided'}</span>
                    )}
                  </dd>
                </div>

                <div>
                  <dt className="text-sm font-medium text-gray-500 flex items-center">
                    <EnvelopeIcon className="h-4 w-4 mr-1" />
                    Email Status
                  </dt>
                  <dd className="mt-1 flex items-center">
                    {customer.emailVerified ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        <CheckCircleIcon className="h-4 w-4 mr-1" />
                        Verified
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        <XCircleIcon className="h-4 w-4 mr-1" />
                        Not Verified
                      </span>
                    )}
                  </dd>
                </div>

                <div>
                  <dt className="text-sm font-medium text-gray-500 flex items-center">
                    <CalendarIcon className="h-4 w-4 mr-1" />
                    Registration Date
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {formatDate(customer.createdAt)}
                  </dd>
                </div>

                <div>
                  <dt className="text-sm font-medium text-gray-500">Terms Accepted</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {customer.acceptedTerms ? 'Yes' : 'No'}
                  </dd>
                </div>
              </dl>
            </div>
          </div>

          {/* Activity Summary */}
          <div className="mt-6 bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-900">Activity Summary</h2>
            </div>
            <div className="px-6 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center">
                  <div className="flex items-center justify-center mb-2">
                    <CameraIcon className="h-8 w-8 text-gray-400" />
                  </div>
                  <div className="text-2xl font-bold text-gray-900">{customer.photoCount || 0}</div>
                  <div className="text-sm text-gray-500">Photos</div>
                </div>
                <div className="text-center">
                  <div className="flex items-center justify-center mb-2">
                    <SparklesIcon className="h-8 w-8 text-gray-400" />
                  </div>
                  <div className="text-2xl font-bold text-gray-900">{customer.sessionCount || 0}</div>
                  <div className="text-sm text-gray-500">Style Sessions</div>
                </div>
              </div>
            </div>
          </div>

          {/* Staff Picks */}
          <div className="mt-6">
            <StaffPicksManager customerId={customer.id} customerName={customer.name} />
          </div>
        </div>

        {/* Store Assignment */}
        <div>
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-900">Store</h2>
            </div>
            <div className="px-6 py-4">
              <div className="mb-2">
                <div className="text-sm font-medium text-gray-700 mb-2">Current Store:</div>
                {customer.location?.name ? (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                    {customer.location.name}
                  </span>
                ) : (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-600">
                    No store (sees full catalogue)
                  </span>
                )}
              </div>

              {canEditStore ? (
                <div className="mt-3">
                  <label htmlFor="customerStore" className="block text-sm font-medium text-gray-700 mb-1">
                    Change store
                  </label>
                  <select
                    id="customerStore"
                    value={customer.locationId || ''}
                    onChange={(e) => handleStoreChange(e.target.value)}
                    disabled={updating}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm disabled:bg-gray-100"
                  >
                    <option value="">No store</option>
                    {locations.map((location) => (
                      <option key={location.id} value={location.id}>
                        {location.name}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-gray-500">
                    Changing the store changes which catalogue the customer sees.
                  </p>
                </div>
              ) : (
                <p className="mt-1 text-xs text-gray-500">
                  Only a super-admin (not assigned to a store) can change a customer's store.
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Catalogue Access Level Management */}
        <div>
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-900">Catalogue Access Level</h2>
            </div>
            <div className="px-6 py-4">
              <div className="mb-4">
                <div className="text-sm font-medium text-gray-700 mb-2">Current Level:</div>
                {customer.catalogAccessLevel === 'exclusive' ? (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800">
                    Exclusive
                  </span>
                ) : customer.catalogAccessLevel === 'partner' ? (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-teal-100 text-teal-800">
                    Partner
                  </span>
                ) : customer.catalogAccessLevel === 'paid_deposit' ? (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-amber-100 text-amber-800">
                    Paid Deposit
                  </span>
                ) : (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                    New
                  </span>
                )}
              </div>

              {customer.catalogAccessLevel === 'partner' ? (
                <p className="text-sm text-gray-600">
                  This is a partner account. Partners see only dresses tagged with the
                  Partner tier; their access level cannot be changed here.
                </p>
              ) : (
              <div className="space-y-2">
                <button
                  onClick={() => handleAccessLevelChange('exclusive')}
                  disabled={updating || customer.catalogAccessLevel === 'exclusive'}
                  className="w-full px-4 py-2 text-sm font-medium text-white bg-purple-600 border border-transparent rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Set as Exclusive
                </button>
                <button
                  onClick={() => handleAccessLevelChange('paid_deposit')}
                  disabled={updating || customer.catalogAccessLevel === 'paid_deposit'}
                  className="w-full px-4 py-2 text-sm font-medium text-white bg-amber-600 border border-transparent rounded-md hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Set as Paid Deposit
                </button>
                <button
                  onClick={() => handleAccessLevelChange('new')}
                  disabled={updating || customer.catalogAccessLevel === 'new'}
                  className="w-full px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Set as New
                </button>
              </div>
              )}

              <div className="mt-4 text-xs text-gray-500">
                {customer.catalogAccessLevel === 'partner'
                  ? 'Partner can view only dresses tagged with the Partner tier.'
                  : customer.catalogAccessLevel === 'exclusive'
                  ? 'Customer can view all dresses including exclusive pieces.'
                  : customer.catalogAccessLevel === 'paid_deposit'
                  ? 'Customer can view dresses in the New and Paid Deposit tiers.'
                  : 'Customer can only view dresses in the New tier.'
                }
              </div>
            </div>
          </div>

          {/* Email Verification Management */}
          {!customer.emailVerified && (
            <div className="mt-6 bg-white shadow rounded-lg">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-medium text-gray-900">Email Verification</h2>
              </div>
              <div className="px-6 py-4 space-y-3">
                <button
                  onClick={handleResendVerification}
                  disabled={updating}
                  className="w-full px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Resend Verification Email
                </button>
                <button
                  onClick={handleManualVerification}
                  disabled={updating}
                  className="w-full px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Verify Manually (Admin)
                </button>
              </div>
            </div>
          )}

          {/* Quick Actions */}
          <div className="mt-6 bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-900">Quick Actions</h2>
            </div>
            <div className="px-6 py-4 space-y-2">
              <Link
                to={`/customers/${customer.id}/preferences`}
                className="block w-full px-4 py-2 text-sm font-medium text-center text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                View Dress Preferences & Liked Dresses
              </Link>
              <Link
                to={`/customers/${customer.id}/photos`}
                className="block w-full px-4 py-2 text-sm font-medium text-center text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                View Photos ({customer.photoCount || 0})
              </Link>
              <Link
                to={`/customers/${customer.id}/style-matcher`}
                className="block w-full px-4 py-2 text-sm font-medium text-center text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Style Matcher Analytics
              </Link>
            </div>
          </div>

          {/* Delete Customer (Admin only) */}
          {user?.role === 'admin' && (
            <div className="mt-6 bg-white shadow rounded-lg border border-red-200">
              <div className="px-6 py-4 border-b border-red-200">
                <h2 className="text-lg font-medium text-red-900">Danger Zone</h2>
              </div>
              <div className="px-6 py-4">
                {deleteConfirm ? (
                  <div className="space-y-3">
                    <p className="text-sm text-red-600">Are you sure you want to delete this customer? This action cannot be undone.</p>
                    <div className="flex space-x-3">
                      <button
                        onClick={handleDelete}
                        disabled={updating}
                        className="flex-1 px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                      >
                        Yes, Delete Customer
                      </button>
                      <button
                        onClick={() => setDeleteConfirm(false)}
                        className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => setDeleteConfirm(true)}
                    className="w-full px-4 py-2 text-sm font-medium text-red-600 bg-white border border-red-300 rounded-md hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                  >
                    <TrashIcon className="h-4 w-4 inline mr-2" />
                    Delete Customer
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomerDetail;